import {
  TFinancialCondition,
  TFinancialConditionLegalResident,
  TFinancialConditionSoleTrader,
  TFundingSources,
} from '~/modules/identification/forms/FinancialCondition/types.ts';
import { TAutocompleteOption } from '~/components/AutocompleteField/types';
import {
  EMPTY_BANK_ACCOUNTS,
  SourcesOfFunds,
} from '~/modules/identification/forms/FinancialCondition/constants.ts';
import { useMemo } from 'react';

export const getFinancialConditionInitialValuesLegalResident = (
  formData: TFinancialConditionLegalResident
) => ({
  noOtherBankAccounts: formData?.noOtherBankAccounts ?? false,
  bankAccounts:
    (formData?.bankAccounts?.length as number) > 0
      ? formData?.bankAccounts?.map((bank) => ({
          accountNumber: bank?.accountNumber || null,
          bankName: bank?.bankName || null,
          bankMFO: bank?.bankMFO || null,
          currency: bank?.currency || null,
        }))
      : [EMPTY_BANK_ACCOUNTS],
  prevDate: prevDateFunction(formData),
  securitiesNominalValue: formData?.securitiesNominalValue || null,
  netIncomeFromSales: formData?.netIncomeFromSales || null,
  totalLoanDebt: formData?.totalLoanDebt || null,
  debtFromNonResidentLoans: formData?.debtFromNonResidentLoans || null,
  costOfGoodsSold: formData?.costOfGoodsSold || null,
  grossProfitOrLoss: formData?.grossProfitOrLoss || null,
  nonCurrentAssets: formData?.nonCurrentAssets || null,
  totalDeposits: formData?.totalDeposits || null,
  currentAssets: formData?.currentAssets || null,
  currentLiabilities: formData?.currentLiabilities || null,
  equity: formData?.equity || null,
  salesExpenses: formData?.salesExpenses || null,
  plannedMonthlyInflows: formData?.plannedMonthlyInflows || null,
  maxMonthlyInflows: formData?.maxMonthlyInflows || null,
  fundingSources: {
    businessActivity: formData?.fundingSources?.businessActivity ?? false,
    financialAssistance: formData?.fundingSources?.financialAssistance ?? false,
    contractAndDerivatives: formData?.fundingSources?.contractAndDerivatives ?? false,
    loan: formData?.fundingSources?.loan ?? false,
    saleOfReceivables: formData?.fundingSources?.saleOfReceivables ?? false,
    saleOfSecurities: formData?.fundingSources?.saleOfSecurities ?? false,
    other: formData?.fundingSources?.other || null,
  },
});

export const getFinancialConditionInitialValuesSoleTrader = (
  formData: TFinancialConditionSoleTrader
) => ({
  noOtherBankAccounts: formData?.noOtherBankAccounts ?? false,
  bankAccounts:
    (formData?.bankAccounts?.length as number) > 0
      ? formData?.bankAccounts?.map((bank) => ({
          accountNumber: bank?.accountNumber || null,
          bankName: bank?.bankName || null,
          bankMFO: bank?.bankMFO || null,
          currency: bank?.currency || null,
        }))
      : [EMPTY_BANK_ACCOUNTS],
  prevDate: prevDateFunction(formData),
  plannedMonthlyInflows: formData?.plannedMonthlyInflows || null,
  prevYearTaxableIncome: formData?.prevYearTaxableIncome || null,
  maxMonthlyInflows: formData?.maxMonthlyInflows || null,
  fundingSources: {
    businessActivity: formData?.fundingSources?.businessActivity ?? false,
    financialAssistance: formData?.fundingSources?.financialAssistance ?? false,
    contractAndDerivatives: formData?.fundingSources?.contractAndDerivatives ?? false,
    loan: formData?.fundingSources?.loan ?? false,
    saleOfReceivables: formData?.fundingSources?.saleOfReceivables ?? false,
    saleOfSecurities: formData?.fundingSources?.saleOfSecurities ?? false,
    other: formData?.fundingSources?.other || null,
  },
});

export const getPrevDateOptions = (): TAutocompleteOption[] => {
  const formatDate = (date: Date) =>
    `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}.${date.getFullYear()}`;

  const lastDayOfPreviousYear = new Date(new Date().getFullYear() - 2, 11, 31);
  const lastDayOfCurrentYear = new Date(new Date().getFullYear() - 1, 11, 31);

  const currentDate = new Date();
  const startOfYear = new Date(currentDate.getFullYear(), 0, 1);
  const daysSinceStartOfYear = Math.floor((+currentDate - +startOfYear) / (1000 * 60 * 60 * 24));

  if (daysSinceStartOfYear < 60) {
    return [
      { value: '0', label: formatDate(lastDayOfPreviousYear) },
      { value: '21', label: formatDate(lastDayOfCurrentYear) },
    ];
  }

  return [{ value: '0', label: formatDate(lastDayOfCurrentYear) }];
};

export const fundingSourcesText = (fundingSourcesObject: TFundingSources | null) =>
  useMemo(() => {
    if (!fundingSourcesObject) return '';

    return Object.entries(fundingSourcesObject)
      .filter(([_, value]) => value === true || (typeof value === 'string' && value))
      .map(([key, value]) => {
        if (key === 'other' && typeof value === 'string') {
          return SourcesOfFunds.other(value);
        }
        return SourcesOfFunds[key as keyof typeof SourcesOfFunds];
      })
      .filter((text): text is string => typeof text === 'string')
      .join('; ');
  }, [fundingSourcesObject]);

export const prevDateFunction = (formData: TFinancialCondition) =>
  (() => {
    const options = getPrevDateOptions();
    if (options.length === 1) return options[0];
    if (formData?.prevDate && options.length > 0) {
      return options.find((opt) => opt.value === formData.prevDate) || null;
    }
    return null;
  })();

export const normalizeZeroValues = <T extends Record<string, unknown>>(values: T): T => {
  const normalize = (value: unknown): unknown => {
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      if (!isNaN(parsed) && parsed === 0) return 0;
      return value;
    }
    if (typeof value === 'number' && value === 0) return 0;
    if (Array.isArray(value)) return value.map(normalize);
    if (value !== null && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, normalize(v)])
      );
    }
    return value;
  };

  return normalize(values) as T;
};
